// ABOUTME: Date selector pairing quick "X months ago" shortcuts with a manual date picker.
// ABOUTME: Emits the chosen date as a YYYY-MM-DD string; used for "exclude counted after" style filters.
import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { subMonths } from "date-fns";
import { useTranslation } from "react-i18next";
import { InputDropdown } from "./dropdowns/InputDropdown";
import ShDateRangePicker from "./ShDateRangePicker";

const formatDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/**
 * ShDateWithMonthShortcuts
 *
 * Props:
 * - value: string | null - Selected date as YYYY-MM-DD
 * - onChange: function - Called with the new YYYY-MM-DD string
 * - months: number[] (optional) - Shortcut amounts, defaults to [1, 2, 3, 4]
 * - noSelection: string (optional) - Placeholder for the shortcut dropdown
 * - size: number (optional) - Date picker size, defaults to 32
 * - sx: object (optional) - Wrapper style overrides
 */
const ShDateWithMonthShortcuts = (props) => {
    const { t } = useTranslation();
    const months = props.months ?? [1, 2, 3, 4];
    const size = props.size ?? 32;

    // Which quick shortcut is active; -1 when a date is picked manually or the value is cleared.
    const [selectedMonth, setSelectedMonth] = useState(-1);

    // The trailing "else" option reveals the manual date picker; the numeric options set the date directly.
    const monthOptions = [
        ...months.map((amount) => ({
            id: amount,
            name: `${amount} ${amount === 1 ? t("month").toLowerCase() : t("months")}`,
            isCustom: false
        })),
        { id: "else", name: t("other"), isCustom: true }
    ];

    const isCustomSelected = selectedMonth > -1 && monthOptions[selectedMonth]?.isCustom;

    // Keep the shortcut dropdown in sync when the value is reset externally (e.g. modal reopened).
    useEffect(() => {
        if (!props.value) {
            setSelectedMonth(-1);
        }
    }, [props.value]);

    const handleSelectMonth = (index) => {
        setSelectedMonth(index);
        if (index === -1) return;
        if (monthOptions[index].isCustom) {
            // Default the manual picker to today so the field is never empty.
            props.onChange(formatDate(new Date()));
        } else {
            props.onChange(formatDate(subMonths(new Date(), monthOptions[index].id)));
        }
    };

    const handleSelectDate = (data) => {
        props.onChange(data[0].formattedStartDate);
    };

    return (
        <Box sx={{ flex: 1, display: "flex", gap: 2, ...props.sx }}>
            <InputDropdown
                displayName="name"
                selected={selectedMonth}
                options={monthOptions}
                noSelection={props.noSelection ?? t("choose_option")}
                onChange={handleSelectMonth}
                closeOnSelection
            />
            {isCustomSelected &&
                <ShDateRangePicker
                    explicit
                    singleSelect={true}
                    startDate={props.value}
                    shouldHide={!props.value}
                    select={handleSelectDate}
                    size={size}
                />
            }
        </Box>
    );
};

export default ShDateWithMonthShortcuts;
